import instance from "@/plugins/axios";
import { ResponseResult } from "@/core/models/response-result";
import {
  AbstractResourceRemoteDataSource,
  AllocateResourceParams,
  GetAvailableResourcesParams,
  UpdateImageParams,
} from "@/modules/resources/remote/AbstractResourceRemoteDataSource";
import { ResourceType } from "@/modules/resources/models/resource-type";
import { ResourceDetail } from "@/modules/resources/models/resource-detail";
import { ResourceResumed } from "../models/resource-resumed";
import { ResourceGeneralInformation } from "../models/resource-general-information";

export class ResourceRemoteDataSource
  implements AbstractResourceRemoteDataSource
{
  async getResources(): Promise<ResponseResult> {
    try {
      const response = (await instance.get<ResponseResult<any>>("Resources"))
        .data;
      if (!response.isSuccess) return response;
      response.data =
        ResourceRemoteDataSource.convertFromJsonToResourceTypeList(
          response.data.types,
        );
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async getResource(id: string): Promise<ResponseResult> {
    try {
      const response = (
        await instance.get<ResponseResult<any>>("Resources/" + id)
      ).data;
      if (!response.isSuccess) return response;
      response.data = ResourceRemoteDataSource.convertFromJsonToResourceDetail(
        response.data,
      );
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }
  async getResourcesStructure(): Promise<ResponseResult> {
    try {
      const response = (
        await instance.get<ResponseResult<any>>("Resources/structure")
      ).data;
      if (!response.isSuccess) return response;
      console.log("response", response);
      response.data =
        ResourceRemoteDataSource.convertFromJsonToResourceTypeList(
          response.data.types,
        );
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async getAvailableResources(
    params: GetAvailableResourcesParams,
  ): Promise<ResponseResult> {
    try {
      const response = (
        await instance.post<ResponseResult<any>>("Resources/available", params)
      ).data;

      if (!response.isSuccess) return response;

      response.data =
        ResourceRemoteDataSource.convertFromJsonToResourceResumedList(
          response.data,
        );
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }
  async allocateResource(
    params: AllocateResourceParams,
    id: string,
  ): Promise<ResponseResult> {
    try {
      const response = (
        await instance.post<ResponseResult<any>>(
          "Resources/events/" + id + "/allocations",
          params,
        )
      ).data;
      if (!response.isSuccess) return response;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async getGeneralInformation(): Promise<ResponseResult> {
    try {
      const response = (
        await instance.get<ResponseResult<any>>("Resources/summary")
      ).data;
      if (!response.isSuccess) return response;
      response.data =
        ResourceRemoteDataSource.convertFromJsonToResourceGeneralInformation(
          response.data,
        );
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async updateImage(
    resourceId: string,
    params: UpdateImageParams,
  ): Promise<ResponseResult> {
    try {
      const formData = new FormData();
      formData.append("file", params.file);
      const response = (
        await instance.post<ResponseResult<any>>(
          "Resources/" + resourceId + "/image",
          formData,
        )
      ).data;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async deleteImage(resourceId: string): Promise<ResponseResult> {
    try {
      const response = (
        await instance.delete<ResponseResult<any>>(
          "Resources/" + resourceId + "/image",
        )
      ).data;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  static convertFromJsonToResourceTypeList(data: any[]): ResourceType[] {
    return data.map((resource: any) =>
      this.convertFromJsonToResourceType(resource),
    );
  }

  static convertFromJsonToResourceType(resourceType: any): ResourceType {
    return {
      id: resourceType.id,
      quantity: resourceType.quantity,
      name: resourceType.name,
      subTypes: resourceType.subTypes.map((subType: any) => ({
        id: subType.id,
        quantity: subType.quantity,
        name: subType.name,
        resources: subType.resources.map((resourceDetail: any) =>
          this.convertFromJsonToResourceDetail(resourceDetail),
        ),
      })),
    };
  }

  static convertFromJsonToResourceDetail(data: any): ResourceDetail {
    return {
      id: data.id,
      resourceId: data.resourceId,
      name: data.name,
      cost: data.cost ?? data.associatedCost,
      quantity: data.quantity,
      maxQuantity: data.maxQuantity,
      type: data.type,
      assemblyTime: data.assemblyTime,
      disassemblyTime: data.disassemblyTime,
      resourcePermanency: data.resourcePermanency,
      imageVersion: data.imageVersion,
    };
  }

  static convertFromJsonToResourceResumedList(data: any[]): ResourceResumed[] {
    return data.map((resource: any) =>
      this.convertFromJsonToResourceResumed(resource),
    );
  }

  static convertFromJsonToResourceResumed(data: any): ResourceResumed {
    return {
      id: data.resourceId,
      name: data.name,
      cost: data.price,
      quantity: data.availableQuantity,
      imageVersion: data.imageVersion,
    };
  }

  static convertFromJsonToResourceGeneralInformation(
    data: any,
  ): ResourceGeneralInformation {
    return {
      activeResources: data.activeResources,
      available: data.available,
      inUse: data.inUse,
      inMaintenance: data.inMaintenance,
      averageCost: data.averageCost,
    };
  }
}
